// Domain-specific mutation functions for Kiddom API
// Generic GraphQL mutation infrastructure moved to infrastructure/api/graphql/
// This file contains domain-specific mutation implementations

// Import generic GraphQL infrastructure from infrastructure layer
import {
  doAdminQueries,
  doAdminQueriesWithResult,
  doPaginationQuery,
  doAdminMutation,
  type IQueryInfo,
  type DataResponse,
  type IQueryParam,
} from 'infrastructure/api'

// Import domain-specific resource definitions
import {
  AddSchoolFeature,
  AddUser,
  DeleteDistrict,
  DeleteManyUsers,
  DeleteSchool,
  FixDistrictAdminRoles,
  MigrateSchoolCurriculaToDistrictLevel,
  MigrateVersionInfo,
  RemoveDuplicateIDFromSchool,
  RemoveSchoolFeature,
  UpdateSchoolInformation,
  UpdateUserSchool,
  UpdateDistrict,
  AddCurricula,
  EditUser,
  ResetPassword,
  DeleteManyDemos,
  GetLTIIntegration,
  CreateLTIRegistration,
  CreateLTIIssuer,
  GetLTIIssuers,
  UpdateLTIRegistration,
  UpdateClassArchiveStatus,
  ResubmitAttempts,
  GetSISDistrictInfo,
  SetSISDistrictInfo,
  curriculumsAddedOnSchoolLevel,
  LMSDistrictInfo,
  UpdateLMSSettings,
  TKATEventInfo,
  CreateDemo,
  ReinstateDemos,
  ReinstateDistricts,
  ReinstateSchools,
  ReinstateUsers,
  SetProductPlatformFeature,
  ResetCustomLessonsForClass,
} from './resources'

// Import API types
import {
  SchoolInformationRequest,
  DistrictUpdateRequest,
  IEditUser,
  ICreateUser,
  ICreateLTIRegistration,
  ICreateLTIIssuer,
  ILTIRegistration,
  ISISDistrictInfo,
  ICurriculaInSchoolLevel,
  ILMSDistrictInfo,
  ITKATEventInfo,
  IDemoCreate,
} from 'infrastructure/api'

// Re-export infrastructure types and functions for backward compatibility
export {
  doAdminQueries,
  doAdminQueriesWithResult,
  doPaginationQuery,
  doAdminMutation,
  type IQueryInfo,
  type DataResponse,
  type IQueryParam,
}
// function supports multiple ids
export function deleteSchool(schoolID: number[], deleteUsers: boolean) {
  return doAdminMutation(DeleteSchool, {
    school_ids: schoolID,
    delete_users: deleteUsers,
  })
}
// function supports multiple ids
export function deleteDistrict(
  districtID: number[],
  deleteSchools: boolean,
  deleteUsers: boolean,
) {
  return doAdminMutation(DeleteDistrict, {
    district_ids: districtID,
    delete_schools: deleteSchools,
    delete_users: deleteUsers,
  })
}

export function deleteManyUsers(userIDs: number[]) {
  return doAdminMutation(DeleteManyUsers, { user_ids: userIDs })
}

export function deleteManyDemos(demoIDs: number[]) {
  return doAdminMutation(DeleteManyDemos, { demo_ids: demoIDs })
}

export function addSchoolFeature(schoolId: number[], featureId: number) {
  return doAdminMutation(AddSchoolFeature, {
    school_ids: schoolId,
    feature_id: featureId,
  })
}

export function removeSchoolFeature(schoolIds: number[], featureId: number) {
  return doAdminMutation(RemoveSchoolFeature, {
    school_ids: schoolIds,
    feature_id: featureId,
  })
}

export function setProductPlatformFeature(
  productId: number,
  platformFeatureId: number,
  isEnabled: boolean,
) {
  return doAdminMutation(SetProductPlatformFeature, {
    product_id: productId,
    platform_feature_id: platformFeatureId,
    is_enabled: isEnabled,
  })
}

export function resetCustomLessonsForClass(classId: number) {
  return doAdminMutation(ResetCustomLessonsForClass, {
    class_id: classId,
  })
}

export function updateSchoolInformation(
  schoolId: number,
  params: SchoolInformationRequest,
) {
  const paramsToSend: any = { school_id: schoolId }
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null) {
      paramsToSend[key] = value
    }
  })
  return doAdminMutation(UpdateSchoolInformation, paramsToSend)
}

export function updateUserSchool(userID: number, schoolID: number) {
  return doAdminMutation(UpdateUserSchool, {
    user_id: userID,
    school_id: schoolID,
  })
}

export function updateDistrict(districtUpdateRequest: DistrictUpdateRequest) {
  const params: { [key: string]: any } = {}
  Object.entries(districtUpdateRequest).forEach(([key, value]) => {
    if (value !== null) {
      params[key] = value
    }
  })
  return doAdminMutation(UpdateDistrict, params)
}

type NewProductParams = {
  tree_id: string
  version: string
  title: string
  standards_body: string
  version_name?: string
  vanity_url_code?: string
  partner_id: number
  subject_id: number
  grade_id: number
}
export async function publishNewProduct(params: NewProductParams) {
  const queryResource: IQueryInfo = {
    resourceData: {
      key: 'publishNewProduct',
    },
    params,
  }
  const result = await doAdminQueries<string>([queryResource])
  return {
    result: result.data.publishNewProduct,
    errors: result.errors,
  }
}

export interface IMasterInfo {
  treeId: string
  version: string
  copiedTrees: string[]
}

export interface IVersionInfo {
  treeId: string
  version: string
  name: string
  title: string
}

export interface IToUpdateInfo {
  name: string
  version: string
}

export interface IUpdateInfo {
  // To Version
  destinationVersion: IVersionInfo

  // From Version
  versionToUpdate: IToUpdateInfo
}

export async function migrateVersions(toUpdate: IUpdateInfo[]) {
  const queries: IQueryInfo[] = []
  toUpdate.forEach((updateInfo) => {
    const fromVersion = updateInfo.versionToUpdate.version
    queries.push({
      resourceData: MigrateVersionInfo,
      params: {
        tree_id: updateInfo.destinationVersion.treeId,
        from_version: fromVersion,
        to_version: updateInfo.destinationVersion.version,
      },
      alias: `v_${fromVersion.replaceAll('-', '')}`,
    })
  })
  return doAdminQueries(queries)
}

export async function addUser(user: ICreateUser) {
  const params: { user: ICreateUser } = {
    user,
  }
  const query = { resourceData: AddUser, params }
  return doAdminQueries<{ message: string; Value: number }>([query])
}

interface IMigrateSchoolCurriculaToDistrict {
  masterTreeId: string
  masterVersion: string
  districtId: number
}

export async function migrateSchoolCurriculaToDistrict(
  migrateCurricula: IMigrateSchoolCurriculaToDistrict,
) {
  return doAdminMutation(MigrateSchoolCurriculaToDistrictLevel, {
    master_tree_id: migrateCurricula.masterTreeId,
    master_version_id: migrateCurricula.masterVersion,
    district_id: migrateCurricula.districtId,
  })
}

export function removeDuplicateIDFromSchool() {
  const query: IQueryInfo = {
    resourceData: RemoveDuplicateIDFromSchool,
    params: {},
  }
  return doAdminQueries([query]).then(({ data }) => {
    return {
      data,
    }
  })
}

export function fixDistrictAdminRoles() {
  const query: IQueryInfo = {
    resourceData: FixDistrictAdminRoles,
    params: {},
  }
  return doAdminQueries([query]).then(({ data }) => {
    return {
      data,
    }
  })
}

export function addCurricula(
  productList: number[],
  districtID?: number,
  schoolID?: number,
) {
  const params: { [key: string]: any } = {}
  if (schoolID) {
    params['school_id'] = schoolID
  }
  if (districtID) {
    params['district_id'] = districtID
  }
  params['product_ids'] = productList

  const query: IQueryInfo = {
    resourceData: AddCurricula,
    params: params,
  }
  return doAdminQueries([query], true)
}

export function editUser(user: IEditUser) {
  const params: { edit_user: IEditUser } = {
    edit_user: user,
  }

  const query: IQueryInfo = {
    resourceData: EditUser,
    params,
  }
  return doAdminQueries([query], true)
}

export function resetPassword(userID: number) {
  const params: { user_id: number } = {
    user_id: userID,
  }

  const query: IQueryInfo = {
    resourceData: ResetPassword,
    params,
  }
  return doAdminQueries([query], true)
}

export async function getLTIIntegration(
  district_id: number,
): Promise<ILTIRegistration> {
  const query: IQueryInfo = {
    resourceData: GetLTIIntegration,
    params: { district_id },
  }
  const resp = await doAdminQueries<ILTIRegistration>([query], true)
  if (resp.errors) {
    throw new Error(resp.errors[0].message)
  }
  return resp.data?.[GetLTIIntegration.key]
}

export function createLTIRegistration(
  lti_registration: ICreateLTIRegistration,
) {
  const query: IQueryInfo = {
    resourceData: CreateLTIRegistration,
    params: { lti_registration },
  }
  return doAdminQueries([query], false)
}

export function updateLTIRegistration(
  lti_registration: ICreateLTIRegistration,
  registration_id: number,
) {
  const query: IQueryInfo = {
    resourceData: UpdateLTIRegistration,
    params: { lti_registration, registration_id },
  }
  return doAdminQueries([query], false)
}

export function createLTIIssuer(lti_issuer: ICreateLTIIssuer) {
  const query: IQueryInfo = {
    resourceData: CreateLTIIssuer,
    params: { lti_issuer },
  }
  return doAdminQueries([query], false)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function getLTIIssuers<T = any>(): Promise<Record<string, T>> {
  const query: IQueryInfo = {
    resourceData: GetLTIIssuers,
    params: {},
  }
  const resp = await doAdminQueries<T>([query], true)
  if (resp.errors) {
    throw new Error(resp.errors[0].message)
  }

  return resp.data
}

export function updateClassArchiveStatus(
  id: number,
  level: string,
  archive: boolean,
) {
  const query: IQueryInfo = {
    resourceData: UpdateClassArchiveStatus,
    params: { id, level, archive },
  }
  return doAdminQueries([query], false)
}

export function resubmitAttempts(district_id: number) {
  const query: IQueryInfo = {
    resourceData: ResubmitAttempts,
    params: { district_id },
  }
  return doAdminQueries([query], false)
}

export async function getSISDistrictInfo(
  district_id: number,
): Promise<ISISDistrictInfo> {
  const query: IQueryInfo = {
    resourceData: GetSISDistrictInfo,
    params: { district_id },
  }
  const resp = await doAdminQueries<ISISDistrictInfo>([query], true)
  if (resp.errors) {
    throw new Error(resp.errors[0].message)
  }
  return resp.data?.[GetSISDistrictInfo.key]
}

export function setSISDistrictInfo(sis_info: ISISDistrictInfo) {
  const query: IQueryInfo = {
    resourceData: SetSISDistrictInfo,
    params: { sis_info },
  }
  return doAdminQueries([query], false)
}

export async function getcurriculumsAddedOnSchoolLevel(
  district_id: number,
): Promise<ICurriculaInSchoolLevel[]> {
  const query: IQueryInfo = {
    resourceData: curriculumsAddedOnSchoolLevel,
    params: { district_id },
  }
  const resp = await doAdminQueries<ICurriculaInSchoolLevel[]>([query], true)
  if (resp.errors) {
    throw new Error(resp.errors[0].message)
  }
  return resp.data?.[curriculumsAddedOnSchoolLevel.key]
}

export async function getLMSDistrictInfo(
  district_id: number,
): Promise<ILMSDistrictInfo> {
  const query: IQueryInfo = {
    resourceData: LMSDistrictInfo,
    params: { district_id },
  }
  const resp = await doAdminQueries<ILMSDistrictInfo>([query], true)
  if (resp.errors) {
    throw new Error(resp.errors[0].message)
  }
  return resp.data?.[LMSDistrictInfo.key]
}

export function updateLMSSettings(
  integration_id: number,
  is_daily_sync_enabled: boolean,
) {
  const query: IQueryInfo = {
    resourceData: UpdateLMSSettings,
    params: { integration_id, is_daily_sync_enabled },
  }
  return doAdminQueries([query], false)
}

export async function getTKATEventInfo(
  target_id: string,
  action: string,
  target_type: string,
  exclude_completed: boolean,
): Promise<ITKATEventInfo[]> {
  const query: IQueryInfo = {
    resourceData: TKATEventInfo,
    params: { target_id, action, target_type, exclude_completed },
  }
  const resp = await doAdminQueries<ITKATEventInfo[]>([query], true)
  if (resp.errors) {
    throw new Error(resp.errors[0].message)
  }
  return resp.data?.[TKATEventInfo.key]
}

export async function getDemoTypes() {
  return doAdminMutation<string[]>({ key: 'demoTypes' }, {})
}

export async function createDemo(demo: IDemoCreate): Promise<number> {
  const query: IQueryInfo = {
    resourceData: CreateDemo,
    params: { demo_info: demo },
  }
  const resp = await doAdminQueries<number>([query], true)
  if (resp.errors) {
    throw new Error(resp.errors[0].message)
  }
  return resp.data?.[CreateDemo.key]
}

export async function cloneDemo(demoID: number, schoolID: number) {
  return doAdminMutation<number>(
    { key: 'demoClone' },
    { demo_id: demoID, school_id: schoolID },
  )
}

export async function softDeleteExpiredDemos() {
  return doAdminMutation<string>({ key: 'deleteExpiredDemos' }, {})
}

export async function getExpiredDemoCount() {
  return doAdminMutation<number>({ key: 'getExpiredDemoCount' }, {})
}

export function reinstateSchools(params: {
  school_ids: number[]
  reinstate_users: boolean
}) {
  return doAdminMutation(ReinstateSchools, params)
}

export function reinstateDemos(params: { demo_ids: number[] }) {
  return doAdminMutation(ReinstateDemos, params)
}

export function reinstateDistricts(params: {
  district_ids: number[]
  reinstate_schools: boolean
  reinstate_users: boolean
}) {
  return doAdminMutation(ReinstateDistricts, params)
}

export function reinstateUsers(params: { user_ids: number[] }) {
  return doAdminMutation(ReinstateUsers, params)
}
